import * as React from 'react';
import { useFilters } from '@/hooks';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/common/typography';
import { Icon } from '@/components/common/icon';
import Icons from '@/const/icons';
import { InputDate } from '@/components/form';
import { useForm } from 'react-hook-form';
import { format, parseISO, parse, isValid } from 'date-fns';
import { useRouter } from 'next/router';
import useModal from '@/hooks/useModal';
import { DocumentFilterPanel } from './document-filter-follow-up/DocumentFilterPanel';
import { useFollowUpFilter } from '@/hooks/filters/useFollowUpFilter';
import { useClaimFormFilter } from '@/hooks/filters/useClaimFormFilter';

type FilterSource = 'FOLLOW_UP' | 'CLAIM_FORM';

export const AddFilter = () => {
	const { register } = useForm();
	const { setParamSeveralFilters, params } = useFilters();
	const [isOpen, setIsOpen] = React.useState(false);
	const router = useRouter();
	const { id: caseId } = router.query;
	const { Modal, hide, show } = useModal();
	const [filterSource, setFilterSource] =
		React.useState<FilterSource>('FOLLOW_UP');

	const followUp = useFollowUpFilter(caseId as string | undefined);
	const claimForm = useClaimFormFilter(caseId as string | undefined);
	const active = filterSource === 'CLAIM_FORM' ? claimForm : followUp;

	const modalTitle =
		filterSource === 'CLAIM_FORM'
			? 'Select the Claim Form'
			: 'Select Responses';

	const openPanel = (source: FilterSource) => {
		setFilterSource(source);
		show();
	};

	const currentAfter = params.get('dateFrom') || '';
	const currentBefore = params.get('dateTo') || '';
	const currentMessageFilter = params.get('messageFilter') || '';

	const initialAfter = React.useMemo(() => {
		if (!currentAfter) return '';
		const date = parse(currentAfter, 'yyyy-MM-dd', new Date());
		if (isValid(date)) return format(date, 'MM-dd-yyyy');
		const isoDate = parseISO(currentAfter);
		if (isValid(isoDate)) return format(isoDate, 'MM-dd-yyyy');
		return '';
	}, [currentAfter]);

	const initialBefore = React.useMemo(() => {
		if (!currentBefore) return '';
		const date = parse(currentBefore, 'yyyy-MM-dd', new Date());
		if (isValid(date)) return format(date, 'MM-dd-yyyy');
		const isoDate = parseISO(currentBefore);
		if (isValid(isoDate)) return format(isoDate, 'MM-dd-yyyy');
		return '';
	}, [currentBefore]);

	const [after, setAfter] = React.useState<string>(initialAfter);
	const [before, setBefore] = React.useState<string>(initialBefore);

	React.useEffect(() => {
		if (isOpen) {
			setAfter(initialAfter);
			setBefore(initialBefore);
		}
	}, [isOpen, initialAfter, initialBefore]);

	const handleApplyDates = () => {
		setParamSeveralFilters([
			{
				label: 'dateFrom',
				value: after
					? format(parse(after, 'MM-dd-yyyy', new Date()), 'yyyy-MM-dd')
					: undefined,
			},
			{
				label: 'dateTo',
				value: before
					? format(parse(before, 'MM-dd-yyyy', new Date()), 'yyyy-MM-dd')
					: undefined,
			},
		]);
		setIsOpen(false);
	};

	const handleMessageFilter = (value: string) => {
		const newValue = currentMessageFilter === value ? undefined : value;
		setParamSeveralFilters([{ label: 'messageFilter', value: newValue }]);
		setIsOpen(false);
	};

	return (
		<>
			<Popover open={isOpen} onOpenChange={setIsOpen}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						className="flex items-center gap-2 h-[33px] px-3 rounded-md border bg-white"
					>
						<Icon src={Icons.filterSearch} className="!w-4 !h-4" />
						<Typography type="semibold-14" className="text-opacity-black">
							Add Filter
						</Typography>
					</Button>
				</PopoverTrigger>
				<PopoverContent
					align="start"
					className="w-[525px] p-4 rounded-3xl shadow-shadow2 bg-white"
					sideOffset={8}
				>
					{/* Last Activity Section */}
					<div className="flex gap-8">
						<div className="flex flex-col mb-4 w-1/2">
							<Typography type="regular-14" className="text-opacity-black mb-3">
								Last Activity
							</Typography>

							<div className="flex flex-col gap-3">
								<InputDate
									name="after"
									title="After"
									register={register}
									setValueInput={(_name: string, value: string) =>
										setAfter(value)
									}
									dateDefault={after}
									placeholder="Select date"
									classNameInput="h-[38px]"
									classNameTitle="text-xs"
								/>

								<InputDate
									name="before"
									title="Before"
									register={register}
									setValueInput={(_name: string, value: string) =>
										setBefore(value)
									}
									dateDefault={before}
									placeholder="Select date"
									classNameInput="h-[38px]"
									classNameTitle="text-xs"
								/>
							</div>

							<div className="flex justify-end mt-3">
								<Button
									variant="ghost"
									className="bg-primary text-white disabled:bg-[#B18EE0] rounded-full px-6 h-[25px] font-semibold"
									onClick={handleApplyDates}
									disabled={
										!after &&
										!before &&
										currentAfter === after &&
										currentBefore === before
									}
								>
									Apply
								</Button>
							</div>
						</div>
						{/* Follow-up Section */}
						<div className="flex flex-col w-1/2">
							<Typography type="regular-14" className="text-opacity-black mb-3">
								Follow-up
							</Typography>

							<div className="flex flex-col gap-2 pl-1">
								<button
									onClick={() => openPanel('FOLLOW_UP')}
									className={`flex items-center justify-between w-full transition-colors ${
										!followUp.hasItems ? 'opacity-50 cursor-not-allowed' : ''
									}`}
									disabled={!followUp.hasItems}
								>
									<div className="flex items-center gap-3">
										<Icon
											src={Icons.settings}
											className="!w-[18px] !h-[18px]"
										/>
										<Typography type="regular-14" className="text-black">
											Set-up
										</Typography>
									</div>
									<Icon
										src={Icons.arrowUpRight}
										className="!w-[18px] !h-[18px] text-black"
									/>
								</button>
							</div>

							<Typography
								type="regular-14"
								className="text-opacity-black mb-3 mt-7"
							>
								Claim Form
							</Typography>

							<div className="flex flex-col gap-2 pl-1">
								<button
									onClick={() => openPanel('CLAIM_FORM')}
									className={`flex items-center justify-between w-full transition-colors ${
										!claimForm.hasItems ? 'opacity-50 cursor-not-allowed' : ''
									}`}
									disabled={!claimForm.hasItems}
								>
									<div className="flex items-center gap-3">
										<Icon
											src={Icons.settings}
											className="!w-[18px] !h-[18px]"
										/>
										<Typography type="regular-14" className="text-black">
											Set-up
										</Typography>
									</div>
									<Icon
										src={Icons.arrowUpRight}
										className="!w-[18px] !h-[18px] text-black"
									/>
								</button>
							</div>
						</div>
					</div>

					{/* Messages Section */}
					<div className="flex flex-col mb-6">
						<Typography type="regular-14" className="text-opacity-black mb-3">
							Messages
						</Typography>

						<div className="flex flex-col gap-4 pl-1">
							<button
								onClick={() => handleMessageFilter('UNREAD_MESSAGES')}
								className="flex items-center gap-3 w-full"
							>
								<Icon src={Icons.message} className="!w-[18px] !h-[18px]" />
								<Typography type="regular-14" className="text-black">
									Unread
								</Typography>
							</button>

							<button
								onClick={() => handleMessageFilter('READ_MESSAGES')}
								className="flex items-center gap-3 w-full"
							>
								<Icon src={Icons.message} className="!w-[18px] !h-[18px]" />
								<Typography type="regular-14" className="text-black">
									Replied to
								</Typography>
							</button>
						</div>
					</div>
				</PopoverContent>
			</Popover>
			<Modal
				title={
					<div className="flex flex-col gap-y-1">
						<span>{modalTitle}</span>
						<span className="text-sm text-opacity-black font-normal">
							Choose which responses apply to the filtering.
						</span>
					</div>
				}
				backgroundColor="white"
				classNameCardContainer="w-full lg:!w-[960px] 2xl:!w-[1182px] !px-0 !py-0 !pt-8"
				classNameContainerChildren="!px-0 !py-0 h-[78vh] 2xl:h-[80vh]"
				classNameTitle="pl-8 text-left !mb-4"
				withXButton
				noMaxHeight
			>
				<DocumentFilterPanel
					followUpsList={active.list}
					isLoading={active.isLoading}
					sidebarTitle={
						filterSource === 'CLAIM_FORM' ? 'Claim Form' : 'Follow-ups'
					}
					onApply={(filters) => {
						active.applyFilter(filters.selectedAnswers);
						hide();
					}}
					onCancel={hide}
				/>
			</Modal>
		</>
	);
};
